import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerLink from '../../Components/CustomerLink';
import { roundLabel, humanCreatedBy } from './reportCodeLabel';
import { matchesPermission } from '../../Utils/userAuthority';

const fmt = (n, decimals = 2) => {
    const num = typeof n === 'string' ? parseFloat(n) : (typeof n === 'number' ? n : 0);
    if (isNaN(num)) return '0.00';
    return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const todayStr = () => {
    const d = new Date();
    const pad = (x) => String(x).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const daysBetween = (fromStr, toStr) => {
    const from = new Date(fromStr);
    const to = new Date(toStr);
    return Math.max(0, Math.round((to - from) / (1000 * 60 * 60 * 24)));
};

// createdAt เป็น LocalDateTime จาก saledata (เวลาเซิร์ฟเวอร์ ICT) → "YYYY-MM-DD HH:mm"
const fmtDateTime = (s) => (s ? String(s).replace('T', ' ').slice(0, 16) : '-');

// จัดการยอดค้าง — Vector 2026-10-02: หนี้เกิดได้ 3 ทาง (คอลัมน์ "ที่มา"): คนขับกดที่หน้าส่งเงินในแอพ IMD,
// กดตอนขาย (SALE_APP) และบัญชีเพิ่มเองจากบิลในหน้านี้ — ค้างทั้งบิล มีผลทันที ไม่มีขั้นรอยืนยัน
// รอบ 2 (Vector 2026-10-07 tg_11726/11730/11734): รวมตามร้าน · "จ่ายแล้ว" ทำได้ครั้งเดียวจากฝั่งไหนก็ได้ (IMD หรือเว็บนี้ — คนกดทีหลังได้ 409)
// ชำระทั้งบิลเท่านั้น · แก้ได้ (ย้อนจ่ายแล้ว/ยกเลิก/ย้ายร้าน) ทุกอย่างมีประวัติ ไม่ลบแถว
// ข้อมูลมาจาก saledata-module (credit_debt)
const SOURCE_LABEL = { SALE_APP: 'ตอนขาย', IMD: 'IMD ส่งเงิน', ACCOUNTING: 'บัญชีเพิ่ม', FRONT_COUNTER: 'ขายหน้าโรง' };
const CHANNEL_LABEL = { IMD: 'IMD', WEB: 'เว็บบัญชี', SYSTEM: 'ระบบ' };
// ช่องทางรับเงินจริงที่บัญชีเลือกตอนกด "ชำระแล้ว" (Vector 2026-10-11) — แถวเก่า/IMD ไม่มีค่า = ไม่ระบุช่องทาง
const METHOD_LABEL = { CASH: '💵 เงินสด', TRANSFER: '🏦 โอน' };
const methodText = (m) => METHOD_LABEL[m] || 'ไม่ระบุช่องทาง';

// ปุ่มเขียนเห็นเมื่อมี report:write · ไม่รู้สิทธิ์ (session เก่าไม่มี permissions) = ให้ server ตัดสินเหมือนเดิม
const canWriteNow = () => {
    try {
        const perms = JSON.parse(localStorage.getItem('user') || '{}')?.permissions;
        return !Array.isArray(perms) || perms.some(g => matchesPermission(g, 'report:write'));
    } catch (e) { return true; }
};
const ACTION_LABEL = { PAY: 'ชำระแล้ว', UNPAY: 'ย้อนการชำระ', CANCEL: 'ยกเลิกค้าง', REOPEN: 'เปิดค้างอีกครั้ง', MOVE_CUSTOMER: 'ย้ายร้าน' };
const FLAG_TEXT = {
    NOT_ALLOWED_AT_RECEIPT: '⚠ ร้านนี้ไม่ได้รับอนุญาตให้ค้างตอนรับบิล (ขาจร/ปิดสิทธิ์แล้ว) — โปรดตรวจ',
    UNVERIFIED: '⚠ ตรวจสิทธิ์ค้างของร้านนี้ไม่ได้ตอนสร้างรายการ — โปรดตรวจ',
};

// ใครกด: tablet:ROLE:<ป้ายบัตร> → ป้ายบัตร · uid ระบบ/ว่าง → "" (ฝั่งเว็บบอกด้วยป้ายฝั่งอยู่แล้ว ไม่โชว์รหัสดิบ)
const actorName = (actor) => {
    const s = String(actor || '').trim();
    const m = /^tablet:[^:]*:(.+)$/i.exec(s);
    if (m) return m[1];
    return humanCreatedBy(s);
};
// newValue ของ PAY = "PAID <CASH|TRANSFER> / <หมายเหตุ>" (แถวเก่าเป็น "PAID" เฉย ๆ)
const payEventText = (ev) => {
    const m = /^PAID (CASH|TRANSFER)(?: \/ (.*))?$/.exec(String(ev.newValue || ''));
    const base = ev.reportCode ? `รอบ ${roundLabel(ev.reportCode)}` : '';
    if (!m) return base;
    return [base, methodText(m[1]) + (m[2] ? ` · ${m[2]}` : '')].filter(Boolean).join(' · ');
};
const withoutCode = (v) => {
    const s = String(v || '');
    const i = s.indexOf(' / ');
    return i >= 0 ? s.slice(i + 3) : s;
};
const errorText = async (res) => {
    const t = await res.text();
    try { const j = JSON.parse(t); if (j && j.error) return j.error; } catch (e) { /* ไม่ใช่ JSON */ }
    return t || `HTTP ${res.status}`;
};

const DebtManagement = () => {
    const navigate = useNavigate();
    const canWrite = canWriteNow();

    const [groups, setGroups] = useState([]);
    const [loadingDebts, setLoadingDebts] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [showPaidHistory, setShowPaidHistory] = useState(false);
    const [paidDebts, setPaidDebts] = useState([]);
    const [showCancelled, setShowCancelled] = useState(false);
    const [cancelledDebts, setCancelledDebts] = useState([]);
    const [expanded, setExpanded] = useState({});
    const [busyDebtId, setBusyDebtId] = useState(null);
    const [busyGroup, setBusyGroup] = useState(null);
    // ประวัติการแก้ของรายการเดียว: { debt, rows }
    const [eventsFor, setEventsFor] = useState(null);
    // กล่องเลือกช่องทางรับเงินตอนกด "ชำระแล้ว": { debt } (รายบิล) หรือ { group } (ทั้งร้าน) · ต้องเลือกเอง ไม่มีค่าเริ่มต้น
    const [payFor, setPayFor] = useState(null);
    const [payMethod, setPayMethod] = useState('');
    const [payNote, setPayNote] = useState('');
    const [paySaving, setPaySaving] = useState(false);
    // ย้ายร้าน: { debt } + รายชื่อร้านให้เลือก
    const [moveFor, setMoveFor] = useState(null);
    const [customers, setCustomers] = useState(null);
    const [customerFilter, setCustomerFilter] = useState('');
    const [moveTo, setMoveTo] = useState('');
    // uid สาย (U-xxx) → ชื่อสาย
    const [routeNames, setRouteNames] = useState({});
    const routeLabel = (id) => (id && routeNames[id]) || id || '-';

    // เพิ่มหนี้จากบิล (บัญชี): เลือกวัน → เลือกสาย/รอบ → เลือกบิล
    const [showAdd, setShowAdd] = useState(false);
    const [addDate, setAddDate] = useState(todayStr());
    const [addReports, setAddReports] = useState([]);
    const [addReportCode, setAddReportCode] = useState('');
    const [addBills, setAddBills] = useState([]);
    const [addDebtByMsd, setAddDebtByMsd] = useState({});
    const [addLoading, setAddLoading] = useState(false);
    const [addError, setAddError] = useState('');
    const [busyMsd, setBusyMsd] = useState(null);

    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) { navigate('/login'); return null; }
        return JSON.parse(user)?.token;
    }, [navigate]);

    const fetchJson = useCallback(async (path) => {
        const token = checkToken();
        if (!token) return null;
        const res = await fetch(`${API_BASE_URL}/saledata/api/module/credit-debts${path}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        return Array.isArray(data) ? data : [];
    }, [checkToken]);

    const fetchOutstandingDebts = useCallback(async () => {
        setLoadingDebts(true);
        setLoadError('');
        try {
            const data = await fetchJson('/by-customer');
            if (data) setGroups(data);
        } catch (e) {
            // ห้ามแสดง "ไม่มีรายการค้าง" เมื่อโหลดไม่สำเร็จ — บัญชีจะเข้าใจผิดว่าไม่มีหนี้
            setLoadError('โหลดรายการค้างไม่สำเร็จ: ' + (e.message || ''));
        } finally {
            setLoadingDebts(false);
        }
    }, [fetchJson]);

    const fetchHistory = useCallback(async () => {
        try {
            const [paid, cancelled] = await Promise.all([fetchJson('?status=PAID'), fetchJson('?status=CANCELLED')]);
            if (paid) setPaidDebts(paid);
            if (cancelled) setCancelledDebts(cancelled);
        } catch (e) {
            setLoadError('โหลดประวัติไม่สำเร็จ: ' + (e.message || ''));
        }
    }, [fetchJson]);

    const reloadAll = useCallback(async () => {
        await fetchOutstandingDebts();
        await fetchHistory();
    }, [fetchOutstandingDebts, fetchHistory]);

    const allDebts = [...groups.flatMap(g => g.debts || []), ...paidDebts, ...cancelledDebts];

    // แปลง uid สาย → ชื่อ ด้วย endpoint เดียวกับหน้าลูกค้า (customer seller-names, มีบน prod แล้ว) — ถามเฉพาะ uid ที่ยังไม่รู้ชื่อ
    useEffect(() => {
        const ids = [...new Set(allDebts.map(d => d.sellerId).filter(Boolean))].filter(id => !(id in routeNames));
        if (ids.length === 0) return;
        const token = checkToken();
        if (!token) return;
        fetch(`${API_BASE_URL}/customer/api/customers/seller-names`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(ids),
        })
            .then(res => (res.ok ? res.json() : []))
            .then(list => {
                const map = {};
                ids.forEach(id => { map[id] = id; }); // ไม่ได้ชื่อ → เก็บรหัสไว้ ไม่ถามซ้ำ
                (Array.isArray(list) ? list : []).forEach(n => { if (n.Seller_ID && n.nameuser) map[n.Seller_ID] = n.nameuser; });
                setRouteNames(prev => ({ ...prev, ...map }));
            })
            .catch(() => { /* โหลดชื่อไม่ได้ → แสดงรหัสสายตามเดิม */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, paidDebts, cancelledDebts, routeNames, checkToken]);

    useEffect(() => {
        reloadAll();
    }, [reloadAll]);

    // เรียกแก้ไขรายการ — 409 (มีอีกฝั่งทำไปก่อน) แสดงข้อความ server ตรง ๆ แล้วรีเฟรชให้เห็นสถานะจริง
    const act = async (debt, path, method, body, failText) => {
        const token = checkToken();
        if (!token) return false;
        setBusyDebtId(debt.id);
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/credit-debts${path}`, {
                method,
                headers: { Authorization: `Bearer ${token}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
                ...(body ? { body: JSON.stringify(body) } : {}),
            });
            if (!res.ok) {
                const msg = await errorText(res);
                await reloadAll();
                throw new Error(msg);
            }
            await reloadAll();
            return true;
        } catch (e) {
            alert(`${failText}: ${e.message || ''}`);
            return false;
        } finally {
            setBusyDebtId(null);
        }
    };

    const markPaid = (debt) => {
        setPayMethod('');
        setPayNote('');
        setPayFor({ debt });
    };

    const unpay = (debt) => {
        if (!window.confirm(`ย้อน "จ่ายแล้ว" ของ "${debt.customerName || 'ไม่ทราบชื่อ'}" ${fmt(debt.amount)} บาท กลับเป็นค้าง?`)) return;
        return act(debt, `/${debt.id}/unpay`, 'PUT', null, 'ย้อนการชำระไม่สำเร็จ');
    };

    const cancelDebt = (debt) => {
        if (!window.confirm(`ยกเลิกรายการค้างของ "${debt.customerName || 'ไม่ทราบชื่อ'}" ${fmt(debt.amount)} บาท? (บิลจะกลับเป็นเงินสดที่ต้องส่ง · เก็บประวัติไว้)`)) return;
        return act(debt, `/${debt.id}`, 'DELETE', null, 'ยกเลิกรายการค้างไม่สำเร็จ');
    };

    const payWholeShop = (g) => {
        if (!g.customerId) return;
        setPayMethod('');
        setPayNote('');
        setPayFor({ group: g });
    };

    // ยืนยันในกล่องเลือกช่องทาง — เลือกครั้งเดียวต่อชุด (ทั้งร้าน = ทุกบิลใช้ช่องทางเดียวกัน)
    const confirmPay = async () => {
        if (!payFor || !payMethod) return;
        const token = checkToken();
        if (!token) return;
        const note = payNote.trim();
        const body = { method: payMethod, ...(note ? { note } : {}) };
        if (payFor.debt) {
            setPaySaving(true);
            const ok = await act(payFor.debt, `/${payFor.debt.id}/pay`, 'PUT', body, 'บันทึกการชำระไม่สำเร็จ');
            setPaySaving(false);
            if (ok) setPayFor(null);
            return;
        }
        const g = payFor.group;
        setPaySaving(true);
        setBusyGroup(g.customerId);
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/credit-debts/pay-customer`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ customerId: g.customerId, ...body }),
            });
            if (!res.ok) throw new Error(await errorText(res));
            const out = await res.json();
            if (out.skipped && out.skipped.length > 0) {
                alert(`ชำระแล้ว ${out.paidIds.length} บิล · ข้าม ${out.skipped.length} บิลที่มีคนจัดการไปก่อน:\n` + out.skipped.map(x => x.reason).join('\n'));
            }
            setPayFor(null);
            await reloadAll();
        } catch (e) {
            await reloadAll();
            alert('ชำระครบทั้งร้านไม่สำเร็จ: ' + (e.message || ''));
        } finally {
            setBusyGroup(null);
            setPaySaving(false);
        }
    };

    const showEvents = async (debt) => {
        const token = checkToken();
        if (!token) return;
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/credit-debts/${debt.id}/events`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error(await errorText(res));
            setEventsFor({ debt, rows: await res.json() });
        } catch (e) {
            alert('โหลดประวัติไม่สำเร็จ: ' + (e.message || ''));
        }
    };

    const openMove = async (debt) => {
        setMoveFor({ debt });
        setMoveTo('');
        setCustomerFilter('');
        if (customers) return;
        const token = checkToken();
        if (!token) return;
        try {
            const res = await fetch(`${API_BASE_URL}/customer/api/customers/by-category`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'ALL' }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const list = [];
            (data?.regular || []).forEach(c => { if (c.customerUid) list.push({ id: c.customerUid, name: c.storeName || c.customerName || 'ไม่ทราบชื่อ' }); });
            (data?.agent || []).forEach(c => { if (c.agentCustomerUid) list.push({ id: c.agentCustomerUid, name: c.agentName || c.storeName || 'ไม่ทราบชื่อ' }); });
            setCustomers(list);
        } catch (e) {
            setCustomers([]);
            alert('โหลดรายชื่อร้านไม่สำเร็จ: ' + (e.message || ''));
        }
    };

    const confirmMove = async () => {
        if (!moveFor || !moveTo) return;
        const target = (customers || []).find(c => c.id === moveTo);
        if (!window.confirm(`ย้ายบิลนี้ (${fmt(moveFor.debt.amount)} บาท) จาก "${moveFor.debt.customerName || 'ไม่ทราบชื่อ'}" ไปที่ "${target ? target.name : ''}"? (แก้ที่ตัวบิลด้วย · เก็บประวัติไว้)`)) return;
        const ok = await act(moveFor.debt, `/${moveFor.debt.id}/customer`, 'PUT', { customerId: moveTo }, 'ย้ายร้านไม่สำเร็จ');
        if (ok) setMoveFor(null);
    };

    const authHeaders = (token, extra) => ({ Authorization: `Bearer ${token}`, ...(extra || {}) });

    const loadAddReports = async () => {
        const token = checkToken();
        if (!token) return;
        setAddLoading(true);
        setAddError('');
        setAddReportCode('');
        setAddBills([]);
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/reports/by-date?startDate=${addDate}&endDate=${addDate}&isAgent=false`, { headers: authHeaders(token) });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setAddReports(Array.isArray(data) ? data : []);
        } catch (e) {
            setAddReports([]);
            setAddError('โหลดรายการสาย/รอบไม่สำเร็จ: ' + (e.message || ''));
        } finally {
            setAddLoading(false);
        }
    };

    const loadAddBills = async (reportCode) => {
        setAddReportCode(reportCode);
        setAddBills([]);
        if (!reportCode) return;
        const token = checkToken();
        if (!token) return;
        setAddLoading(true);
        setAddError('');
        try {
            const rc = encodeURIComponent(reportCode);
            const [billsRes, debtsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/saledata/api/module/reports/${rc}/sales-list`, { headers: authHeaders(token) }),
                fetch(`${API_BASE_URL}/saledata/api/module/credit-debts?reportCode=${rc}`, { headers: authHeaders(token) }),
            ]);
            if (!billsRes.ok) throw new Error(`บิล HTTP ${billsRes.status}`);
            if (!debtsRes.ok) throw new Error(`รายการค้าง HTTP ${debtsRes.status}`);
            const bills = await billsRes.json();
            const debts = await debtsRes.json();
            const byMsd = {};
            (Array.isArray(debts) ? debts : []).forEach(d => { byMsd[d.msdUid] = d; });
            setAddBills(Array.isArray(bills) ? bills : []);
            setAddDebtByMsd(byMsd);
        } catch (e) {
            setAddError('โหลดบิลของรอบไม่สำเร็จ: ' + (e.message || ''));
        } finally {
            setAddLoading(false);
        }
    };

    const addDebtFromBill = async (bill) => {
        const token = checkToken();
        if (!token) return;
        const name = bill.customerName || 'ไม่ทราบชื่อ';
        if (!window.confirm(`เพิ่มหนี้ค้างทั้งบิล "${name}" ${fmt(bill.total)} บาท?`)) return;
        setBusyMsd(bill.msdUid);
        try {
            const res = await fetch(`${API_BASE_URL}/saledata/api/module/credit-debts`, {
                method: 'POST',
                headers: authHeaders(token, { 'Content-Type': 'application/json' }),
                body: JSON.stringify({ msdUid: bill.msdUid, source: 'ACCOUNTING' }),
            });
            if (!res.ok) { const t = await res.text(); throw new Error(t || `HTTP ${res.status}`); }
            await loadAddBills(addReportCode);
            await reloadAll();
        } catch (e) {
            alert('เพิ่มหนี้ไม่สำเร็จ: ' + (e.message || ''));
        } finally {
            setBusyMsd(null);
        }
    };

    const totalOutstanding = groups.reduce((s, g) => s + (g.totalAmount || 0), 0);
    const totalBills = groups.reduce((s, g) => s + (g.billCount || 0), 0);

    const rowStyle = { borderBottom: '1px solid #e9ecef', padding: '6px 12px' };
    const headerStyle = { background: '#f8f9fa', fontWeight: 700, borderBottom: '2px solid #dee2e6', padding: '8px 12px', fontSize: '13px', color: '#495057' };
    const sectionStyle = { background: '#fff', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.10)', marginBottom: '24px', overflow: 'hidden' };
    const sectionHeaderStyle = { background: '#e05252', color: '#fff', padding: '10px 16px', fontWeight: 700, fontSize: '14px' };
    const smallBtn = (bg) => ({ backgroundColor: bg, padding: '4px 10px', fontSize: '12px', marginLeft: 6 });
    const groupKey = (g) => g.customerId || `nocust-${g.customerName}`;
    const paidBy = (d) => `${actorName(d.paidBy) || 'บัญชี'} · ฝั่ง ${CHANNEL_LABEL[d.paidChannel] || d.paidChannel || '-'}`;
    const cancelledBy = (d) => `${actorName(d.cancelledBy) || 'บัญชี'} · ฝั่ง ${CHANNEL_LABEL[d.cancelledChannel] || d.cancelledChannel || '-'}`;

    return (
        <div className="btc-container" style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
                <button className="btc-btn" style={{ background: '#6c757d', padding: '4px 10px', fontSize: '13px' }} onClick={() => navigate(-1)}>← กลับ</button>
                <h1 style={{ margin: 0, fontSize: '18px', flex: 1 }}>จัดการยอดค้าง</h1>
                <button className="btc-btn" style={{ background: '#46af38', padding: '4px 12px', fontSize: '13px' }} onClick={() => setShowAdd(v => !v)}>
                    เพิ่มหนี้จากบิล
                </button>
                <button className="btc-btn" style={{ background: '#E60012', padding: '4px 12px', fontSize: '13px' }} onClick={() => navigate('/credit-customers')}>
                    ลูกค้าที่ค้างได้
                </button>
            </div>

            {loadError && <p className="error" style={{ padding: '8px 16px' }}>{loadError}</p>}

            {showAdd && (
                <div style={sectionStyle}>
                    <div style={{ ...sectionHeaderStyle, background: '#46af38' }}>เพิ่มหนี้จากบิล (ค้างทั้งบิล · มีผลทันที)</div>
                    <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                        <label>วันที่ <input type="date" value={addDate} onChange={e => setAddDate(e.target.value)} /></label>
                        <button className="btc-btn" style={{ padding: '4px 12px', fontSize: 13 }} onClick={loadAddReports} disabled={addLoading}>ค้นหาสาย/รอบ</button>
                        <select value={addReportCode} onChange={e => loadAddBills(e.target.value)} disabled={addReports.length === 0}>
                            <option value="">— เลือกสาย / รอบ —</option>
                            {addReports.map(r => (
                                <option key={r.reportCode} value={r.reportCode}>
                                    {(r.nameuser || r.sellerId || '-')} · {addDate} · รอบที่ {r.round ?? '-'}
                                </option>
                            ))}
                        </select>
                    </div>
                    {addError && <p className="error" style={{ padding: '0 16px' }}>{addError}</p>}
                    {addReportCode && (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...headerStyle, textAlign: 'center' }}>เวลา</th>
                                    <th style={{ ...headerStyle, textAlign: 'left' }}>ร้าน</th>
                                    <th style={{ ...headerStyle, textAlign: 'right' }}>ถุง</th>
                                    <th style={{ ...headerStyle, textAlign: 'right' }}>ยอด (฿)</th>
                                    <th style={{ ...headerStyle, width: 130 }}>ค้าง</th>
                                </tr>
                            </thead>
                            <tbody>
                                {addBills.map(b => {
                                    const debt = addDebtByMsd[b.msdUid];
                                    return (
                                        <tr key={b.msdUid} style={debt ? { background: '#f1f8ee' } : undefined}>
                                            <td style={{ ...rowStyle, textAlign: 'center' }}>{b.time ? String(b.time).slice(0, 5) : '-'}</td>
                                            <td style={rowStyle}>{b.customerId ? <CustomerLink name={b.customerName} code={b.customerId} /> : (b.customerName || '-')}</td>
                                            <td style={{ ...rowStyle, textAlign: 'right' }}>{b.quantity ?? '-'}</td>
                                            <td style={{ ...rowStyle, textAlign: 'right' }}>{fmt(b.total)}</td>
                                            <td style={rowStyle}>
                                                {debt ? (
                                                    <span style={{ color: '#2d7a24', fontWeight: 700 }}>{debt.status === 'PAID' ? 'ชำระแล้ว' : 'ค้างแล้ว'}</span>
                                                ) : (
                                                    <button className="btc-btn btn-sm" style={{ backgroundColor: '#e05252', padding: '4px 10px', fontSize: '12px' }}
                                                        onClick={() => addDebtFromBill(b)} disabled={busyMsd === b.msdUid}>
                                                        {busyMsd === b.msdUid ? '...' : 'เพิ่มเป็นหนี้'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {addBills.length === 0 && !addLoading && !addError && (
                                    <tr><td colSpan="5" style={{ ...rowStyle, textAlign: 'center', color: '#adb5bd' }}>รอบนี้ไม่มีบิล</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ยอดค้างที่ยังไม่ชำระ — รวมตามร้าน กดขยายดูรายบิล */}
            <div style={sectionStyle}>
                <div style={sectionHeaderStyle}>ยอดค้างตามร้าน (ยังไม่ชำระ) — {groups.length} ร้าน · {totalBills} บิล</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                        <tr>
                            <th style={{ ...headerStyle, textAlign: 'left' }}>ร้าน</th>
                            <th style={{ ...headerStyle, textAlign: 'left' }}>สาย</th>
                            <th style={{ ...headerStyle, textAlign: 'right' }}>จำนวนบิล</th>
                            <th style={{ ...headerStyle, textAlign: 'right' }}>ยอดรวม (฿)</th>
                            <th style={{ ...headerStyle, width: 230 }}>จัดการ</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map(g => {
                            const key = groupKey(g);
                            const open = !!expanded[key];
                            return (
                                <React.Fragment key={key}>
                                    <tr style={{ background: open ? '#fff8f8' : undefined }}>
                                        <td style={rowStyle}>
                                            <button
                                                aria-label={open ? 'ย่อ' : 'ขยาย'}
                                                onClick={() => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 6px 0 0', fontSize: 12 }}
                                            >{open ? '▼' : '▶'}</button>
                                            {g.customerId ? <CustomerLink name={g.customerName} code={g.customerId} /> : (g.customerName || '-')}
                                        </td>
                                        <td style={rowStyle}>{(g.sellerIds || []).map(routeLabel).join(', ') || '-'}</td>
                                        <td style={{ ...rowStyle, textAlign: 'right' }}>{g.billCount}</td>
                                        <td style={{ ...rowStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(g.totalAmount)}</td>
                                        <td style={rowStyle}>
                                            {!canWrite ? null : g.customerId ? (
                                                <button className="btc-btn btn-sm" style={{ backgroundColor: '#46af38', padding: '4px 10px', fontSize: '12px' }}
                                                    onClick={() => payWholeShop(g)} disabled={busyGroup === g.customerId}>
                                                    {busyGroup === g.customerId ? '...' : 'ชำระครบทั้งร้าน'}
                                                </button>
                                            ) : <span style={{ color: '#adb5bd', fontSize: 11 }}>ไม่ระบุร้าน — ชำระรายบิล</span>}
                                        </td>
                                    </tr>
                                    {open && (g.debts || []).map(d => {
                                        const days = daysBetween(d.billDate, todayStr());
                                        return (
                                            <tr key={d.id} style={{ background: d.flag ? '#fff3cd' : '#fcfcfc' }}>
                                                <td style={{ ...rowStyle, paddingLeft: 36 }} colSpan={2}>
                                                    บิลวันที่ {d.billDate || '-'} · <span title={d.reportCode || ''}>{roundLabel(d.reportCode, routeLabel(d.sellerId))}</span> · {d.quantity ?? '-'} ถุง
                                                    <div style={{ fontSize: 11, color: '#6c757d' }}>
                                                        {SOURCE_LABEL[d.source] || d.source || '-'}{humanCreatedBy(d.createdBy) ? ` · ${humanCreatedBy(d.createdBy)}` : ''} · กดค้างเมื่อ {fmtDateTime(d.createdAt)} ·{' '}
                                                        <span style={{ color: days >= 7 ? '#dc3545' : '#6c757d', fontWeight: days >= 7 ? 700 : 400 }}>ค้างมา {days} วัน</span>
                                                    </div>
                                                    {d.flag && <div style={{ color: '#b45309', fontSize: 11, fontWeight: 700 }}>{FLAG_TEXT[d.flag] || `⚠ ${d.flag} — โปรดตรวจ`}</div>}
                                                </td>
                                                <td style={rowStyle}></td>
                                                <td style={{ ...rowStyle, textAlign: 'right' }}>{fmt(d.amount)}</td>
                                                <td style={rowStyle}>
                                                    {canWrite && <>
                                                    <button className="btc-btn btn-sm" style={smallBtn('#46af38')} onClick={() => markPaid(d)} disabled={busyDebtId === d.id}>
                                                        {busyDebtId === d.id ? '...' : 'ชำระแล้ว'}
                                                    </button>
                                                    <button className="btc-btn btn-sm" style={smallBtn('#6c757d')} onClick={() => cancelDebt(d)} disabled={busyDebtId === d.id}>ยกเลิก</button>
                                                    <button className="btc-btn btn-sm" style={smallBtn('#0d6efd')} onClick={() => openMove(d)} disabled={busyDebtId === d.id}>ย้ายร้าน</button>
                                                    </>}
                                                    <button className="btc-btn btn-sm" style={smallBtn('#adb5bd')} onClick={() => showEvents(d)}>ประวัติ</button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                        {groups.length === 0 && !loadingDebts && !loadError && (
                            <tr><td colSpan="5" style={{ ...rowStyle, textAlign: 'center', color: '#adb5bd' }}>ไม่มีรายการค้าง</td></tr>
                        )}
                    </tbody>
                    <tfoot>
                        <tr style={{ background: '#e9ecef', fontWeight: 700 }}>
                            <td style={{ ...rowStyle, fontWeight: 700 }} colSpan="3">รวมยอดค้างทั้งหมด</td>
                            <td style={{ ...rowStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(totalOutstanding)}</td>
                            <td style={rowStyle}></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* ประวัติที่ชำระแล้ว — ใครกด/ฝั่งไหน/เมื่อไหร่ · ย้อนได้ */}
            <div style={sectionStyle}>
                <div
                    style={{ ...sectionHeaderStyle, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#6c757d' }}
                    onClick={() => setShowPaidHistory(v => !v)}
                >
                    <span>ประวัติที่ชำระแล้ว ({paidDebts.length} รายการ)</span>
                    <span>{showPaidHistory ? '▲ ย่อ' : '▼ ขยาย'}</span>
                </div>
                {showPaidHistory && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr>
                                <th style={{ ...headerStyle, textAlign: 'left' }}>ร้าน</th>
                                <th style={{ ...headerStyle, textAlign: 'right' }}>ยอดเงิน (฿)</th>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>วันที่บิล</th>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>ช่องทางรับเงิน</th>
                                <th style={{ ...headerStyle, textAlign: 'left' }}>ชำระโดย</th>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>เวลาที่ชำระ</th>
                                <th style={{ ...headerStyle, width: 170 }}>จัดการ</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paidDebts.map(d => (
                                <tr key={d.id}>
                                    <td style={rowStyle}>{d.customerId ? <CustomerLink name={d.customerName} code={d.customerId} /> : (d.customerName || '-')}</td>
                                    <td style={{ ...rowStyle, textAlign: 'right' }}>{fmt(d.amount)}</td>
                                    <td style={{ ...rowStyle, textAlign: 'center' }}>{d.billDate}</td>
                                    <td style={{ ...rowStyle, textAlign: 'center' }} data-testid={`paid-method-${d.id}`}>
                                        {methodText(d.paidMethod)}
                                        {d.paidNote ? <div style={{ fontSize: 11, color: '#6c757d' }}>{d.paidNote}</div> : null}
                                    </td>
                                    <td style={rowStyle}>{paidBy(d)}{d.paidReportCode ? <div style={{ fontSize: 11, color: '#6c757d' }}>เก็บในรอบ {roundLabel(d.paidReportCode, routeLabel(d.sellerId))}</div> : null}</td>
                                    <td style={{ ...rowStyle, textAlign: 'center' }}>{fmtDateTime(d.paidAt)}</td>
                                    <td style={rowStyle}>
                                        {canWrite && <button className="btc-btn btn-sm" style={smallBtn('#e0a800')} onClick={() => unpay(d)} disabled={busyDebtId === d.id}>ย้อนจ่ายแล้ว</button>}
                                        <button className="btc-btn btn-sm" style={smallBtn('#adb5bd')} onClick={() => showEvents(d)}>ประวัติ</button>
                                    </td>
                                </tr>
                            ))}
                            {paidDebts.length === 0 && (
                                <tr><td colSpan="7" style={{ ...rowStyle, textAlign: 'center', color: '#adb5bd' }}>ยังไม่มีประวัติ</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* ที่ยกเลิกแล้ว — แถวไม่ถูกลบ */}
            <div style={sectionStyle}>
                <div
                    style={{ ...sectionHeaderStyle, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#adb5bd' }}
                    onClick={() => setShowCancelled(v => !v)}
                >
                    <span>ที่ยกเลิกแล้ว ({cancelledDebts.length} รายการ)</span>
                    <span>{showCancelled ? '▲ ย่อ' : '▼ ขยาย'}</span>
                </div>
                {showCancelled && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr>
                                <th style={{ ...headerStyle, textAlign: 'left' }}>ร้าน</th>
                                <th style={{ ...headerStyle, textAlign: 'right' }}>ยอดเงิน (฿)</th>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>วันที่บิล</th>
                                <th style={{ ...headerStyle, textAlign: 'left' }}>ยกเลิกโดย</th>
                                <th style={{ ...headerStyle, textAlign: 'center' }}>เวลาที่ยกเลิก</th>
                                <th style={{ ...headerStyle, width: 90 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {cancelledDebts.map(d => (
                                <tr key={d.id}>
                                    <td style={rowStyle}>{d.customerId ? <CustomerLink name={d.customerName} code={d.customerId} /> : (d.customerName || '-')}</td>
                                    <td style={{ ...rowStyle, textAlign: 'right' }}>{fmt(d.amount)}</td>
                                    <td style={{ ...rowStyle, textAlign: 'center' }}>{d.billDate}</td>
                                    <td style={rowStyle}>{cancelledBy(d)}</td>
                                    <td style={{ ...rowStyle, textAlign: 'center' }}>{fmtDateTime(d.cancelledAt)}</td>
                                    <td style={rowStyle}><button className="btc-btn btn-sm" style={smallBtn('#adb5bd')} onClick={() => showEvents(d)}>ประวัติ</button></td>
                                </tr>
                            ))}
                            {cancelledDebts.length === 0 && (
                                <tr><td colSpan="6" style={{ ...rowStyle, textAlign: 'center', color: '#adb5bd' }}>ไม่มีรายการที่ยกเลิก</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* เลือกช่องทางรับเงินก่อนบันทึก "ชำระแล้ว" — บังคับเลือก ไม่มีค่าเริ่มต้น */}
            {payFor && (
                <div role="dialog" aria-label="เลือกช่องทางรับเงิน" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: 8, padding: 16, minWidth: 360, maxWidth: 480 }}>
                        <h3 style={{ marginTop: 0 }}>ชำระแล้ว — {(payFor.debt ? payFor.debt.customerName : payFor.group.customerName) || 'ไม่ทราบชื่อ'}</h3>
                        <div style={{ marginBottom: 10, fontSize: 13 }}>
                            {payFor.debt
                                ? <>ยอด <b>{fmt(payFor.debt.amount)}</b> บาท (ชำระทั้งบิล ทำได้ครั้งเดียว)</>
                                : <>ชำระครบทั้งร้าน <b>{payFor.group.billCount}</b> บิล รวม <b>{fmt(payFor.group.totalAmount)}</b> บาท — ใช้ช่องทางเดียวกันทุกบิล</>}
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>รับเงินทางไหน? (ต้องเลือก)</div>
                        <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                            {['CASH', 'TRANSFER'].map(m => (
                                <button key={m} type="button" data-testid={`pay-method-${m}`} aria-pressed={payMethod === m}
                                    onClick={() => setPayMethod(m)}
                                    style={{ flex: 1, padding: '10px 8px', fontSize: 15, fontWeight: 700, borderRadius: 8, cursor: 'pointer',
                                        border: payMethod === m ? '2px solid #2d7a24' : '2px solid #ced4da',
                                        background: payMethod === m ? '#e8f5e3' : '#fff', color: '#212529' }}>
                                    {METHOD_LABEL[m]}
                                </button>
                            ))}
                        </div>
                        <label style={{ display: 'block', fontSize: 12, color: '#495057', marginBottom: 12 }}>
                            {payMethod === 'TRANSFER' ? 'หมายเหตุ / อ้างอิงสลิป (ไม่บังคับ)' : 'หมายเหตุ (ไม่บังคับ)'}
                            <input type="text" value={payNote} maxLength={255} onChange={e => setPayNote(e.target.value)}
                                data-testid="pay-note" style={{ width: '100%', padding: '6px 8px', marginTop: 4, boxSizing: 'border-box' }} />
                        </label>
                        <div style={{ textAlign: 'right' }}>
                            <button className="btc-btn" style={{ background: '#6c757d', padding: '4px 12px', fontSize: 13, marginRight: 8 }} onClick={() => setPayFor(null)} disabled={paySaving}>ยกเลิก</button>
                            <button className="btc-btn" data-testid="pay-confirm" style={{ background: payMethod ? '#46af38' : '#adb5bd', padding: '4px 12px', fontSize: 13 }}
                                onClick={confirmPay} disabled={!payMethod || paySaving}>{paySaving ? '...' : 'บันทึกชำระแล้ว'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ประวัติการแก้ไขของรายการเดียว */}
            {eventsFor && (
                <div role="dialog" aria-label="ประวัติการแก้ไข" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: 8, padding: 16, minWidth: 420, maxWidth: 640, maxHeight: '80vh', overflow: 'auto' }}>
                        <h3 style={{ marginTop: 0 }}>ประวัติ — {eventsFor.debt.customerName || 'ไม่ทราบชื่อ'} · {fmt(eventsFor.debt.amount)} บาท</h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead><tr>
                                <th style={{ ...headerStyle, textAlign: 'left' }}>เวลา</th>
                                <th style={{ ...headerStyle, textAlign: 'left' }}>การกระทำ</th>
                                <th style={{ ...headerStyle, textAlign: 'left' }}>ใคร / ฝั่ง</th>
                                <th style={{ ...headerStyle, textAlign: 'left' }}>ค่าเดิม → ค่าใหม่</th>
                            </tr></thead>
                            <tbody>
                                <tr>
                                    <td style={rowStyle}>{fmtDateTime(eventsFor.debt.createdAt)}</td>
                                    <td style={rowStyle}>เกิดรายการค้าง</td>
                                    <td style={rowStyle}>{SOURCE_LABEL[eventsFor.debt.source] || eventsFor.debt.source || '-'}{humanCreatedBy(eventsFor.debt.createdBy) ? ` · ${humanCreatedBy(eventsFor.debt.createdBy)}` : ''}</td>
                                    <td style={rowStyle}></td>
                                </tr>
                                {eventsFor.rows.map(ev => (
                                    <tr key={ev.id}>
                                        <td style={rowStyle}>{fmtDateTime(ev.createdAt)}</td>
                                        <td style={rowStyle}>{ACTION_LABEL[ev.action] || ev.action}</td>
                                        <td style={rowStyle}>{actorName(ev.actor) || 'บัญชี'} · {CHANNEL_LABEL[ev.channel] || ev.channel || '-'}</td>
                                        <td style={rowStyle}>{ev.action === 'MOVE_CUSTOMER' ? `${withoutCode(ev.oldValue)} → ${withoutCode(ev.newValue)}` : ev.action === 'PAY' ? payEventText(ev) : (ev.reportCode ? `รอบ ${roundLabel(ev.reportCode)}` : '')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div style={{ textAlign: 'right', marginTop: 12 }}>
                            <button className="btc-btn" style={{ background: '#6c757d', padding: '4px 12px', fontSize: 13 }} onClick={() => setEventsFor(null)}>ปิด</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ย้ายร้าน */}
            {moveFor && (
                <div role="dialog" aria-label="ย้ายร้าน" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: 8, padding: 16, minWidth: 380 }}>
                        <h3 style={{ marginTop: 0 }}>ย้ายบิลไปร้านอื่น</h3>
                        <p style={{ margin: '4px 0 8px' }}>จาก "{moveFor.debt.customerName || 'ไม่ทราบชื่อ'}" · {fmt(moveFor.debt.amount)} บาท</p>
                        <input type="text" placeholder="ค้นหาชื่อร้าน" value={customerFilter} onChange={e => setCustomerFilter(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
                        <select size={8} value={moveTo} onChange={e => setMoveTo(e.target.value)} style={{ width: '100%' }} aria-label="เลือกร้านปลายทาง">
                            {(customers || []).filter(c => !customerFilter || c.name.includes(customerFilter)).slice(0, 200).map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                        {customers === null && <p style={{ color: '#6c757d' }}>กำลังโหลดรายชื่อร้าน...</p>}
                        <div style={{ textAlign: 'right', marginTop: 12 }}>
                            <button className="btc-btn" style={{ background: '#6c757d', padding: '4px 12px', fontSize: 13, marginRight: 8 }} onClick={() => setMoveFor(null)}>ยกเลิก</button>
                            <button className="btc-btn" style={{ background: '#0d6efd', padding: '4px 12px', fontSize: 13 }} onClick={confirmMove} disabled={!moveTo}>ย้ายร้าน</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DebtManagement;
